import { useMemo, useState } from "react";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { PageHeader } from "@/components/common/PageHeader";
import { KpiCard } from "@/components/common/KpiCard";
import { useBranch } from "@/auth/BranchContext";
import { fetchGroups } from "@/api/groups";
import { fetchAttendanceMatrix, type AttendanceMatrix } from "@/api/attendance";
import {
  ATTENDANCE_MARKABLE,
  ATTENDANCE_STATUS_COLORS,
  ATTENDANCE_STATUS_LABELS,
  isPresentStatus,
  type AttendanceStatus,
} from "@/types/enums";
import { MarkLessonDialog } from "./MarkLessonDialog";

function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function shortDate(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}`;
}

const stickyCell = {
  position: "sticky" as const,
  left: 0,
  zIndex: 2,
  minWidth: 210,
  maxWidth: 210,
  bgcolor: "background.paper",
};

export function AttendanceMatrixPage() {
  const { effectiveBranchId } = useBranch();
  const queryClient = useQueryClient();
  const [month, setMonth] = useState(currentMonth());
  const [groupFilter, setGroupFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<AttendanceStatus | "">("");
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [markGroupId, setMarkGroupId] = useState<string | null>(null);

  const { data: groups } = useQuery({
    queryKey: ["groups", effectiveBranchId],
    queryFn: () => fetchGroups({ branchId: effectiveBranchId, limit: 200 }),
  });

  const visibleGroups = useMemo(() => {
    const all = groups?.items ?? [];
    return groupFilter ? all.filter((g) => g.id === groupFilter) : all;
  }, [groups, groupFilter]);

  const matrixQueries = useQueries({
    queries: visibleGroups.map((g) => ({
      queryKey: ["attendance-matrix", g.id, month],
      queryFn: () => fetchAttendanceMatrix(g.id, month),
    })),
  });

  const loading = matrixQueries.some((q) => q.isLoading);
  const matrices = matrixQueries.map((q) => q.data).filter(Boolean) as AttendanceMatrix[];
  const maxLessons = matrices.reduce((max, m) => Math.max(max, m.lessons.length), 0);

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ["attendance-matrix"] });

  const stats = useMemo(() => {
    const records = matrices.flatMap((m) =>
      m.lessons.filter((l) => !l.canceled).flatMap((l) => Object.values(l.records)),
    );
    const present = records.filter((r) => isPresentStatus(r.status)).length;
    return {
      students: matrices.reduce((sum, m) => sum + m.roster.length, 0),
      rate: records.length ? Math.round((present / records.length) * 100) : 0,
    };
  }, [matrices]);

  const todayIso = new Date().toISOString().slice(0, 10);
  const todayGroups = useMemo(
    () => (groups?.items ?? []).filter((g) => g.days?.includes(new Date().getDay())),
    [groups],
  );

  return (
    <>
      <PageHeader
        title="Посещаемость"
        subtitle="Журнал посещаемости по группам за выбранный месяц"
        actions={
          <TextField
            label="Месяц"
            type="month"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          />
        }
      />

      <Stack direction={{ xs: "column", md: "row" }} spacing={2} mb={2}>
        <KpiCard label="Групп" value={String(visibleGroups.length)} hint={groupFilter ? "выбрана одна группа" : "все группы филиала"} />
        <KpiCard label="Учеников" value={String(stats.students)} hint="в выбранных группах" />
        <KpiCard label="Уроков в месяце" value={String(maxLessons)} hint="максимум по группе" />
        <KpiCard label="Посещаемость" value={`${stats.rate}%`} hint="доля отметок «Пришёл»" />
      </Stack>

      <Paper className="crm-data-panel" variant="outlined" sx={{ mb: 2, overflow: "hidden" }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ sm: "center" }}
          sx={{ p: 1.5, borderBottom: 1, borderColor: "divider" }}
        >
          <TextField
            select
            size="small"
            label="Группа"
            value={groupFilter}
            onChange={(e) => setGroupFilter(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">Все группы</MenuItem>
            {(groups?.items ?? []).map((g) => (
              <MenuItem key={g.id} value={g.id}>
                {g.code} · {g.subjectName ?? ""}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Статус"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as AttendanceStatus | "")}
            sx={{ minWidth: 190 }}
          >
            <MenuItem value="">Все статусы</MenuItem>
            {ATTENDANCE_MARKABLE.map((s) => (
              <MenuItem key={s} value={s}>
                {ATTENDANCE_STATUS_LABELS[s]}
              </MenuItem>
            ))}
          </TextField>
          {(groupFilter || statusFilter) && (
            <Button size="small" onClick={() => { setGroupFilter(""); setStatusFilter(""); }}>
              Сбросить
            </Button>
          )}
          <Box flexGrow={1} />
          <Stack direction="row" spacing={1.5} flexWrap="wrap">
            {ATTENDANCE_MARKABLE.map((s) => (
              <Stack key={s} direction="row" spacing={0.5} alignItems="center">
                <Box sx={{ width: 9, height: 9, borderRadius: "3px", bgcolor: ATTENDANCE_STATUS_COLORS[s].fg }} />
                <Typography variant="caption" color="text.secondary">
                  {ATTENDANCE_STATUS_LABELS[s]}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>

        {loading ? (
          <Stack alignItems="center" p={4}>
            <CircularProgress size={26} />
          </Stack>
        ) : (
          <Box sx={{ overflow: "auto", maxHeight: "calc(100vh - 330px)" }}>
            <Table size="small" stickyHeader sx={{ "& td, & th": { whiteSpace: "nowrap" } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...stickyCell, zIndex: 4 }}>Ученик</TableCell>
                  <TableCell>Преподаватель</TableCell>
                  <TableCell>Телефон</TableCell>
                  {Array.from({ length: maxLessons }, (_, i) => (
                    <TableCell key={i} align="center" sx={{ minWidth: 118 }}>
                      Урок {i + 1}
                    </TableCell>
                  ))}
                  <TableCell align="right">Пришёл уроков</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {matrices.map((matrix) => {
                  const isCollapsed = collapsed[matrix.group.id];
                  const roster = statusFilter
                    ? matrix.roster.filter((st) =>
                        matrix.lessons.some((l) => l.records[st.studentId]?.status === statusFilter),
                      )
                    : matrix.roster;
                  if (statusFilter && roster.length === 0) return null;

                  const groupTotal = roster.reduce(
                    (sum, st) =>
                      sum +
                      matrix.lessons.filter((l) => !l.canceled && isPresentStatus(l.records[st.studentId]?.status ?? ""))
                        .length,
                    0,
                  );

                  return (
                    <>
                      <TableRow key={matrix.group.id} sx={{ "& td": { bgcolor: "var(--surface-2)", fontWeight: 600 } }}>
                        <TableCell sx={{ ...stickyCell, bgcolor: "var(--surface-2)" }}>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Box
                              component="button"
                              type="button"
                              onClick={() =>
                                setCollapsed((prev) => ({ ...prev, [matrix.group.id]: !prev[matrix.group.id] }))
                              }
                              sx={{ border: 0, background: "none", cursor: "pointer", p: 0, display: "flex", color: "text.secondary" }}
                            >
                              {isCollapsed ? <ChevronRightIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </Box>
                            <b>{matrix.group.code}</b>
                            <Typography variant="caption" color="text.secondary">
                              {matrix.group.subjectName}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ bgcolor: "var(--surface-2)" }}>
                          {groups?.items.find((g) => g.id === matrix.group.id)?.teacherName ?? "—"}
                        </TableCell>
                        <TableCell sx={{ bgcolor: "var(--surface-2)" }}>
                          <Typography variant="caption" color="text.secondary">
                            {roster.length} чел.
                          </Typography>
                        </TableCell>
                        {Array.from({ length: maxLessons }, (_, i) => {
                          const lesson = matrix.lessons[i];
                          return (
                            <TableCell key={i} align="center" sx={{ bgcolor: "var(--surface-2)" }}>
                              {lesson && (
                                <Typography variant="caption" color="text.secondary">
                                  {shortDate(lesson.date)}
                                  {!lesson.canceled && ` · ${lesson.presentCount}`}
                                </Typography>
                              )}
                            </TableCell>
                          );
                        })}
                        <TableCell align="right" sx={{ bgcolor: "var(--surface-2)" }}>
                          Σ {groupTotal}
                        </TableCell>
                      </TableRow>

                      {!isCollapsed &&
                        roster.map((student) => {
                          const attended = matrix.lessons.filter(
                            (l) => !l.canceled && isPresentStatus(l.records[student.studentId]?.status ?? ""),
                          ).length;
                          return (
                            <TableRow key={`${matrix.group.id}-${student.studentId}`} hover>
                              <TableCell sx={stickyCell}>
                                <Typography fontWeight={600} fontSize={13}>
                                  {student.fullName}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" color="text.secondary">
                                  {groups?.items.find((g) => g.id === matrix.group.id)?.teacherName ?? "—"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" color="text.secondary">
                                  {student.phone}
                                </Typography>
                              </TableCell>
                              {Array.from({ length: maxLessons }, (_, i) => {
                                const lesson = matrix.lessons[i];
                                if (!lesson) return <TableCell key={i} />;
                                if (lesson.canceled) {
                                  return (
                                    <TableCell key={i} align="center">
                                      <Chip size="small" label="Отменён" sx={{ height: 22, fontSize: 11 }} />
                                    </TableCell>
                                  );
                                }
                                const record = lesson.records[student.studentId];
                                if (!record) return <TableCell key={i} />;
                                const colors = ATTENDANCE_STATUS_COLORS[record.status];
                                return (
                                  <TableCell key={i} align="center">
                                    <Box
                                      title={`${shortDate(lesson.date)}${lesson.topic ? ` · ${lesson.topic}` : ""}${
                                        record.grade ? ` · оценка ${record.grade}` : ""
                                      }`}
                                      sx={{
                                        display: "inline-block",
                                        px: 1.1,
                                        py: 0.35,
                                        borderRadius: "6px",
                                        fontSize: 11,
                                        fontWeight: 600,
                                        bgcolor: colors.bg,
                                        color: colors.fg,
                                      }}
                                    >
                                      {ATTENDANCE_STATUS_LABELS[record.status]}
                                    </Box>
                                  </TableCell>
                                );
                              })}
                              <TableCell align="right">
                                <b>{attended}</b>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </>
                  );
                })}
                {matrices.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={maxLessons + 4}>
                      <Typography color="text.secondary" p={2} textAlign="center">
                        Нет данных за этот период
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        )}
      </Paper>

      <Paper className="crm-data-panel" variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" mb={1}>
          <Typography variant="subtitle1" fontWeight={700}>
            Отметить сегодняшний урок
          </Typography>
          <Box flexGrow={1} />
          <Typography variant="caption" color="text.secondary">
            {new Date().toLocaleDateString("ru-RU", { day: "numeric", month: "long" })}
          </Typography>
        </Stack>
        {todayGroups.length === 0 ? (
          <Typography color="text.secondary" py={2} textAlign="center">
            Сегодня занятий нет
          </Typography>
        ) : (
          todayGroups.map((g) => {
            const matrix = matrices.find((m) => m.group.id === g.id);
            const marked = Boolean(matrix?.lessons.some((l) => l.date.slice(0, 10) === todayIso));
            return (
              <Stack
                key={g.id}
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{ py: 1.2, borderBottom: 1, borderColor: "divider" }}
              >
                <Chip size="small" label={g.timeSlot} sx={{ minWidth: 104 }} />
                <Box flexGrow={1} minWidth={0}>
                  <Typography fontWeight={600}>
                    {g.code} · {g.subjectName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {g.roomName ?? ""} · {g.teacherName ?? "—"} · {g.studentsCount ?? 0} учеников
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  color={marked ? "success" : "warning"}
                  variant="outlined"
                  label={marked ? "отмечен" : "не отмечен"}
                />
                <Button size="small" variant={marked ? "outlined" : "contained"} onClick={() => setMarkGroupId(g.id)}>
                  {marked ? "Изменить" : "Отметить"}
                </Button>
              </Stack>
            );
          })
        )}
      </Paper>

      {markGroupId && (
        <MarkLessonDialog
          groupId={markGroupId}
          onClose={() => setMarkGroupId(null)}
          onSaved={() => {
            setMarkGroupId(null);
            refresh();
          }}
        />
      )}
    </>
  );
}
